import { useState } from 'react';
import { Button, Modal, Span } from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';
import { ParcelDimensions } from '../model/consolidator';
import { emptyDimensionsDraft, isValidDimensionsDraft, toParcelDimensions } from '../model/dimensions';
import DimensionsFields from './DimensionsFields';

export default function ParcelDimensionsModal({
  trackNumber,
  weight,
  isLoading,
  onCancel,
  onSubmit,
}: {
  trackNumber: string;
  weight: number;
  isLoading: boolean;
  onCancel: () => void;
  onSubmit: (dimensions: ParcelDimensions) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(emptyDimensionsDraft);
  const valid = isValidDimensionsDraft(draft);
  const submit = () => {
    if (valid && !isLoading) {
      void onSubmit(toParcelDimensions(draft));
    }
  };

  return (
    <Modal
      isOpen
      onClose={() => {
        if (!isLoading) onCancel();
      }}
      isVisibleCloseIcon={false}
      width={{ min: '280px', max: '560px' }}
    >
      <div
        className="ct6-dimensions"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dimensions-title"
        aria-describedby="dimensions-description"
        onKeyDown={(event) => {
          if (event.key !== 'Tab') return;
          const controls = Array.from(
            event.currentTarget.querySelectorAll<HTMLElement>(
              'input:not([disabled]):not([type="hidden"]), button:not([disabled])',
            ),
          ).filter((element) => element.offsetParent !== null);
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
      >
        <div id="dimensions-title">
          <Span fs={24} fw={600}>
            {t('consolidatorTracking.dimensions.title')}
          </Span>
        </div>
        <p id="dimensions-description">{t('consolidatorTracking.dimensions.description')}</p>
        <div className="ct6-dimensionsParcel">
          <Span fs={14}>{t('consolidatorTracking.scan.trackNumberLabel')}</Span>
          <Span fs={18} fw={600}>
            {trackNumber}
          </Span>
          <Span fs={14}>{t('currentWeight', { weight })}</Span>
        </div>
        <DimensionsFields
          idPrefix="parcel"
          value={draft}
          disabled={isLoading}
          autoFocus
          onChange={setDraft}
          onSubmit={submit}
        />
        <p className="ct6-dimensionsHint">{t('consolidatorTracking.dimensions.hint')}</p>
        <div className="ct6-dimensionsActions">
          <Button type="secondary" disabled={isLoading} onClick={onCancel}>
            {t('consolidatorTracking.dimensions.cancel')}
          </Button>
          <Button disabled={!valid || isLoading} loading={isLoading} onClick={submit}>
            {t('consolidatorTracking.dimensions.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
